import React, { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Lock, Target, Gavel, Info, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { currencySymbol, formatCurrency } from "@/lib/currency";
import { Mini } from "./shared";
import { ParticipationDialog } from "./ParticipationDialog";
import { CloseBopDialog } from "./CloseBopDialog";
import { CloseFreightDialog } from "./CloseFreightDialog";
import { AddBiddersDialog } from "./AddBiddersDialog";

const toLocalDatetimeInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // datetime-local needs YYYY-MM-DDTHH:MM in LOCAL time, not UTC
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

const fmtDeadline = (iso) => {
  if (!iso) return "not set";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch { return iso; }
};

export const CreatorView = ({ a, onChange }) => {
  const isReadOnly = a.is_super_admin_view && !a.is_creator;
  const isFreight = a.type === "freight";
  return (
    <>
    <div className="grid lg:grid-cols-3 gap-6" data-testid="creator-view">
      <div className={`${isReadOnly ? "lg:col-span-3" : "lg:col-span-2"} border border-black/10 p-8 bg-[#F8F9FA]`}>
        {isFreight ? (
          <FreightCoverageCard a={a} />
        ) : (
          <>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
              <Lock size={12} weight="bold" /> {a.type === "bop" ? "Lowest Total Bid Value" : "Lowest Bid"} — Identity Hidden
            </div>
            <div className="flex items-baseline gap-4">
              <div className="font-display font-extrabold tracking-tightest leading-none text-6xl lg:text-7xl" data-testid="lowest-bid-value">
                {a.lowest_current_round != null ? formatCurrency(a.lowest_current_round, a.currency) : "—"}
              </div>
              <div className="text-sm text-neutral-600">in Round {a.current_round}</div>
            </div>
            <div className="grid grid-cols-3 mt-8 border-t border-black/10 pt-6">
              <Mini label="Bids · This Round" value={a.round_summary?.find(r => r.round === a.current_round)?.bid_count ?? 0} />
              <Mini label="Lowest Overall" value={a.lowest_overall != null ? formatCurrency(a.lowest_overall, a.currency) : "—"} />
              <Mini label="Invited" value={a.invited_emails?.length ?? a.invited_count ?? 0} />
            </div>
            <div className="mt-6 flex items-start gap-2 text-xs text-neutral-600 border-t border-black/10 pt-4" data-testid="tie-breaker-note">
              <Info size={14} weight="bold" className="mt-0.5 shrink-0 klein-blue" />
              <span>
                <strong>Tie-breaker:</strong> if two or more bidders quote the exact same lowest price, the bid that was <em>submitted first</em> wins. Encourage your vendors to submit early.
              </span>
            </div>
          </>
        )}
        {isReadOnly && (
          <div className="mt-4 flex items-start gap-2 text-xs text-neutral-600 border-t border-black/10 pt-4" data-testid="super-admin-readonly-note">
            <Lock size={14} weight="bold" className="mt-0.5 shrink-0 klein-blue" />
            <span>
              <strong>View-only:</strong> you&apos;re seeing this auction as platform super-admin. Only the auction&apos;s creator ({a.creator_name || "—"}) can start a new round or close it. Use the Peek button to inspect raw bids; the access is recorded in the audit log.
            </span>
          </div>
        )}
      </div>

      {!isReadOnly && (
        <div className="border border-black/10 p-6 flex flex-col gap-3" data-testid="creator-actions">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">Actions</div>
          <NextRoundDialog auction={a} onChange={onChange} />
          <ParticipationDialog
            auctionId={a.id}
            canDisqualify={!isReadOnly && a.status === "open"}
            onDisqualified={onChange}
          />
          {a.status === "open" && (
            <AddBiddersDialog auctionId={a.id} onChange={onChange} />
          )}
          <ExtendDeadlineDialog auction={a} onChange={onChange} />
          {a.type === "bop" ? (
            <CloseBopDialog auctionId={a.id} hasBids={(a.total_bids ?? 0) > 0} onChange={onChange} />
          ) : a.type === "freight" ? (
            <CloseFreightDialog auctionId={a.id} onChange={onChange} />
          ) : (
            <CloseAuctionDialog auctionId={a.id} hasBids={(a.total_bids ?? 0) > 0} onChange={onChange} />
          )}
          <div className="text-xs text-neutral-500 mt-3 leading-relaxed">
            Set a target to open a new bidding round. See participation to track who&apos;s engaged. Extend the deadline to give bidders more time. Close the auction to declare the winner{isFreight ? " and allocate containers" : ""}.
          </div>
        </div>
      )}
    </div>
    {a.type === "bop" && (a.line_items || []).length > 0 && (
      <BopPerLinePreview a={a} />
    )}
    {a.type === "freight" && (a.shipments || []).length > 0 && (
      <FreightPerShipmentPreview a={a} />
    )}
    </>
  );
};

const FreightCoverageCard = ({ a }) => {
  const shipments = a.shipments || [];
  const bidCounts = a.per_shipment_bid_count || {};
  const covered = shipments.filter((s) => (bidCounts[s.shipment_ref] || 0) > 0).length;
  const uncovered = shipments.filter((s) => (bidCounts[s.shipment_ref] || 0) === 0);
  return (
    <>
      <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
        <Lock size={12} weight="bold" /> Coverage — Identities Hidden
      </div>
      <div className="flex items-baseline gap-4">
        <div className="font-display font-extrabold tracking-tightest leading-none text-6xl lg:text-7xl" data-testid="freight-coverage-value">
          {covered}<span className="text-neutral-400 text-4xl">/{shipments.length}</span>
        </div>
        <div className="text-sm text-neutral-600">shipments with ≥1 bid in Round {a.current_round}</div>
      </div>
      <div className="grid grid-cols-3 mt-8 border-t border-black/10 pt-6">
        <Mini label="Bids · This Round" value={a.round_summary?.find((r) => r.round === a.current_round)?.bid_count ?? 0} />
        <Mini label="Uncovered" value={uncovered.length} />
        <Mini label="Invited" value={a.invited_emails?.length ?? a.invited_count ?? 0} />
      </div>
      {uncovered.length > 0 && (
        <div className="mt-6 border-t border-black/10 pt-4 text-xs text-neutral-600 leading-relaxed" data-testid="uncovered-hint">
          <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-1.5">Shipments without any bid</div>
          <div className="font-mono-plex text-sm">
            {uncovered.map((s) => s.shipment_ref).join(", ")}
          </div>
          <div className="mt-2">You&apos;ll need at least one eligible bid on every shipment to close & allocate.</div>
        </div>
      )}
      <div className="mt-6 flex items-start gap-2 text-xs text-neutral-600 border-t border-black/10 pt-4" data-testid="tie-breaker-note">
        <Info size={14} weight="bold" className="mt-0.5 shrink-0 klein-blue" />
        <span>
          <strong>Tie-breaker per shipment:</strong> lowest rate → earliest committed ETD → earliest submission.
          Bidders can cherry-pick which shipments they can lift.
        </span>
      </div>
    </>
  );
};

const FreightPerShipmentPreview = ({ a }) => {
  const perLow = a.per_shipment_lowest || {};
  const perCount = a.per_shipment_bid_count || {};
  const shipments = a.shipments || [];
  return (
    <div className="mt-6 border border-black/10 p-6" data-testid="freight-per-shipment-preview">
      <div className="flex items-baseline justify-between border-b border-black pb-3 mb-0">
        <h3 className="font-display font-extrabold text-2xl tracking-tightest">Per-Shipment Lowest (anonymized)</h3>
        <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{shipments.length} containers</span>
      </div>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead className="border-b border-black/10 text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
            <tr>
              <th className="text-left p-2 w-10">#</th>
              <th className="text-left p-2">Shipment</th>
              <th className="text-left p-2 w-32">Container Pickup</th>
              <th className="text-right p-2 w-32">Lowest Rate</th>
              <th className="text-left p-2 w-32">Best ETD</th>
              <th className="text-right p-2 w-24">Bidders</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map((s, i) => {
              const low = perLow[s.shipment_ref];
              const count = perCount[s.shipment_ref] || 0;
              return (
                <tr key={s.shipment_ref} className={`border-b border-black/5 ${count === 0 ? "bg-red-50/40" : ""}`} data-testid={`freight-perline-row-${s.shipment_ref}`}>
                  <td className="p-2 font-mono-plex text-neutral-500">{i + 1}</td>
                  <td className="p-2 font-mono-plex font-semibold">{s.shipment_ref}</td>
                  <td className="p-2 font-mono-plex text-xs">{s.cargo_ready_date}</td>
                  <td className="p-2 text-right font-mono-plex klein-blue">
                    {low?.rate != null ? formatCurrency(low.rate, a.currency) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="p-2 font-mono-plex text-xs">
                    {low?.etd_committed || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="p-2 text-right font-mono-plex text-xs text-neutral-600">{count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed">
        Cheapest quote across all rounds per shipment. Bidder identities remain hidden until you close & allocate.
      </p>
    </div>
  );
};

const BopPerLinePreview = ({ a }) => {
  const perLow = a.per_line_lowest || {};
  const perCount = a.per_line_bid_count || {};
  return (
    <div className="mt-6 border border-black/10 p-6" data-testid="bop-per-line-preview">
      <div className="flex items-baseline justify-between border-b border-black pb-3 mb-0">
        <h3 className="font-display font-extrabold text-2xl tracking-tightest">Per-Line Lowest (anonymized)</h3>
        <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{(a.line_items || []).length} items</span>
      </div>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead className="border-b border-black/10 text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
            <tr>
              <th className="text-left p-2 w-10">#</th>
              <th className="text-left p-2">Item</th>
              <th className="text-left p-2 w-16">UOM</th>
              <th className="text-right p-2 w-28">Forecast Qty</th>
              <th className="text-right p-2 w-40">Lowest Unit Price</th>
              <th className="text-right p-2 w-40">Lowest Line Total</th>
              <th className="text-right p-2 w-20">Bidders</th>
            </tr>
          </thead>
          <tbody>
            {(a.line_items || []).map((li, i) => {
              const low = perLow[li.item_number];
              const lineTotal = low != null ? low * Number(li.forecast_qty || 0) : null;
              return (
                <tr key={li.item_number} className="border-b border-black/5" data-testid={`bop-perline-row-${li.item_number}`}>
                  <td className="p-2 font-mono-plex text-neutral-500">{i + 1}</td>
                  <td className="p-2">
                    <div className="font-mono-plex font-semibold">{li.item_number}</div>
                    <div className="text-xs text-neutral-600">{li.item_desc}</div>
                  </td>
                  <td className="p-2 text-xs">{li.uom || "—"}</td>
                  <td className="p-2 text-right font-mono-plex">{Number(li.forecast_qty).toLocaleString()}</td>
                  <td className="p-2 text-right font-mono-plex klein-blue">
                    {low != null ? formatCurrency(low, a.currency) : "—"}
                  </td>
                  <td className="p-2 text-right font-mono-plex">
                    {lineTotal != null ? formatCurrency(lineTotal, a.currency) : "—"}
                  </td>
                  <td className="p-2 text-right font-mono-plex text-xs text-neutral-600">{perCount[li.item_number] ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed">
        These are the lowest unit prices quoted across all rounds, per item — bidder identities remain hidden.
        Use <strong>Close &amp; Award</strong> to see the full per-bidder matrix (still anonymized as B1, B2…) and award each item to a vendor of your choice.
      </p>
    </div>
  );
};

const NextRoundDialog = ({ auction, onChange }) => {
  const isBop = auction.type === "bop";
  const isFreight = auction.type === "freight";
  const lineItems = auction.line_items || [];
  const shipments = auction.shipments || [];
  const currency = auction.currency;
  const auctionId = auction.id;
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [lineTargets, setLineTargets] = useState(() => {
    const init = {};
    for (const li of lineItems) init[li.item_number] = "";
    return init;
  });
  const [shipmentTargets, setShipmentTargets] = useState(() => {
    const init = {};
    for (const s of shipments) init[s.shipment_ref] = "";
    return init;
  });
  const [busy, setBusy] = useState(false);
  const sym = currencySymbol(currency);

  // Live implied total for BOP per-line targets
  const impliedTotal = useMemo(() => {
    if (isBop) {
      let t = 0;
      for (const li of lineItems) {
        const v = parseFloat(lineTargets[li.item_number]);
        if (!Number.isFinite(v) || v <= 0) return null;
        t += v * Number(li.forecast_qty || 0);
      }
      return Math.round(t * 100) / 100;
    }
    if (isFreight) {
      // Sum of per-shipment target rates (1 container each)
      let t = 0;
      for (const s of shipments) {
        const v = parseFloat(shipmentTargets[s.shipment_ref]);
        if (!Number.isFinite(v) || v <= 0) return null;
        t += v;
      }
      return Math.round(t * 100) / 100;
    }
    return null;
  }, [isBop, isFreight, lineTargets, lineItems, shipmentTargets, shipments]);

  const allBopFilled = isBop && lineItems.every((li) => parseFloat(lineTargets[li.item_number]) > 0);
  const allFreightFilled = isFreight && shipments.every((s) => parseFloat(shipmentTargets[s.shipment_ref]) > 0);
  const canSubmit = isBop ? allBopFilled : isFreight ? allFreightFilled : Number(target) > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = isBop
        ? { line_targets: Object.fromEntries(lineItems.map((li) => [li.item_number, Number(lineTargets[li.item_number])])) }
        : isFreight
          ? { shipment_targets: Object.fromEntries(shipments.map((s) => [s.shipment_ref, Number(shipmentTargets[s.shipment_ref])])) }
          : { target_price: Number(target) };
      await api.post(`/auctions/${auctionId}/next-round`, payload);
      toast.success("Next round started · bidders notified");
      setOpen(false);
      setTarget("");
      const reset = {}; lineItems.forEach((li) => { reset[li.item_number] = ""; });
      setLineTargets(reset);
      const resetS = {}; shipments.forEach((s) => { resetS[s.shipment_ref] = ""; });
      setShipmentTargets(resetS);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-11 btn-sharp" data-testid="open-next-round-dialog">
          <Target size={14} weight="bold" className="mr-2" /> Start Next Round
        </Button>
      </DialogTrigger>
      <DialogContent className={`rounded-none ${isBop || isFreight ? "max-w-3xl" : ""}`} data-testid="next-round-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest">
            {isBop ? "Set Target Unit Prices (Per Item)" : isFreight ? "Set Target Rates (Per Shipment)" : "Set Target Price"}
          </DialogTitle>
        </DialogHeader>
        {isBop ? (
          <div className="space-y-3">
            <p className="text-sm text-neutral-700 leading-relaxed">
              Set a target <strong>unit price</strong> for each line item. Bidders will be asked to re-quote at or below these unit prices in the next round.
            </p>
            <div className="overflow-x-auto border border-black/10 max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="next-round-line-targets-table">
                <thead className="bg-[#F8F9FA] border-b border-black/10 sticky top-0">
                  <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                    <th className="text-left p-2 w-10">#</th>
                    <th className="text-left p-2">Item</th>
                    <th className="text-left p-2 w-16">UOM</th>
                    <th className="text-right p-2 w-24">Qty</th>
                    <th className="text-right p-2 w-36">Target ({sym}/unit) *</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, i) => (
                    <tr key={li.item_number} className="border-b border-black/5" data-testid={`next-round-target-row-${li.item_number}`}>
                      <td className="p-2 font-mono-plex text-neutral-500">{i + 1}</td>
                      <td className="p-2">
                        <div className="font-mono-plex font-semibold">{li.item_number}</div>
                        <div className="text-xs text-neutral-600">{li.item_desc}</div>
                      </td>
                      <td className="p-2 text-xs">{li.uom || "—"}</td>
                      <td className="p-2 text-right font-mono-plex text-xs">{Number(li.forecast_qty).toLocaleString()}</td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          value={lineTargets[li.item_number]}
                          onChange={(e) => setLineTargets({ ...lineTargets, [li.item_number]: e.target.value })}
                          placeholder="0.00"
                          className="rounded-none border-black/20 h-9 text-right font-mono-plex"
                          data-testid={`next-round-target-input-${li.item_number}`}
                          required
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-black bg-[#F8F9FA]">
                  <tr>
                    <td colSpan={4} className="p-2 text-right text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                      Implied total target
                    </td>
                    <td className="p-2 text-right font-mono-plex text-lg klein-blue" data-testid="next-round-implied-total">
                      {impliedTotal != null ? formatCurrency(impliedTotal, currency) : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[11px] text-neutral-500 leading-relaxed">
              The implied total is for your reference. Each bidder must beat the target <strong>per line</strong> — they can&apos;t average out across items.
            </p>
          </div>
        ) : isFreight ? (
          <div className="space-y-3">
            <p className="text-sm text-neutral-700 leading-relaxed">
              Set a target <strong>rate</strong> for each shipment. Bidders will be asked to re-quote at or below these rates in the next round.
            </p>
            <div className="overflow-x-auto border border-black/10 max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="next-round-shipment-targets-table">
                <thead className="bg-[#F8F9FA] border-b border-black/10 sticky top-0">
                  <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                    <th className="text-left p-2 w-10">#</th>
                    <th className="text-left p-2">Shipment</th>
                    <th className="text-left p-2 w-32">Container Pickup</th>
                    <th className="text-right p-2 w-40">Target Rate ({sym}) *</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s, i) => (
                    <tr key={s.shipment_ref} className="border-b border-black/5" data-testid={`next-round-target-row-${s.shipment_ref}`}>
                      <td className="p-2 font-mono-plex text-neutral-500">{i + 1}</td>
                      <td className="p-2 font-mono-plex font-semibold">{s.shipment_ref}</td>
                      <td className="p-2 font-mono-plex text-xs">{s.cargo_ready_date}</td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          value={shipmentTargets[s.shipment_ref]}
                          onChange={(e) => setShipmentTargets({ ...shipmentTargets, [s.shipment_ref]: e.target.value })}
                          placeholder="0.00"
                          className="rounded-none border-black/20 h-9 text-right font-mono-plex"
                          data-testid={`next-round-target-input-${s.shipment_ref}`}
                          required
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-black bg-[#F8F9FA]">
                  <tr>
                    <td colSpan={3} className="p-2 text-right text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                      Implied auction total
                    </td>
                    <td className="p-2 text-right font-mono-plex text-lg klein-blue" data-testid="next-round-implied-total">
                      {impliedTotal != null ? formatCurrency(impliedTotal, currency) : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Target Price ({sym})</Label>
            <Input type="number" step="0.01" value={target} onChange={(e) => setTarget(e.target.value)} className="rounded-none h-11" data-testid="next-round-target-input" />
            <p className="text-xs text-neutral-600">All bidders will see this target and get a chance to re-quote in a new round.</p>
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !canSubmit} className="rounded-none bg-[#0B1929] text-white btn-sharp" data-testid="next-round-confirm">
            {busy ? "Starting..." : "Open New Round"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ExtendDeadlineDialog = ({ auction, onChange }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(() => toLocalDatetimeInput(auction.deadline));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const history = auction.deadline_history || [];

  const submit = async () => {
    if (!val) {
      toast.error("Pick a new deadline first");
      return;
    }
    // datetime-local gives us a value in the user's local timezone. Convert to a
    // proper ISO string with timezone so the backend doesn't misinterpret it.
    const isoUtc = new Date(val).toISOString();
    setBusy(true);
    try {
      await api.patch(`/auctions/${auction.id}/deadline`, {
        deadline: isoUtc,
        reason: reason.trim() || null,
      });
      toast.success("Deadline updated — bidders notified");
      setOpen(false);
      setReason("");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update deadline");
    } finally {
      setBusy(false);
    }
  };

  // Minimum datetime for the picker = now + 5 minutes (so admin can't accidentally pick "right now")
  const minDt = (() => {
    const d = new Date(Date.now() + 5 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full rounded-none border-black h-11 btn-sharp"
          data-testid="open-extend-deadline-dialog"
        >
          <Clock size={14} weight="bold" className="mr-2" /> Extend Deadline
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-lg" data-testid="extend-deadline-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest">
            Extend Bidding Deadline
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-neutral-700 leading-relaxed">
            Push the bid-closure deadline forward. All invited bidders will be
            emailed about the new deadline so they can re-quote with more time.
          </div>
          <div className="border border-black/10 p-3 bg-[#F8F9FA]">
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Current Deadline</div>
            <div className="font-mono-plex text-sm" data-testid="current-deadline-display">
              {fmtDeadline(auction.deadline)}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">
              New Deadline <span className="text-red-600">*</span>
            </Label>
            <Input
              type="datetime-local"
              value={val}
              min={minDt}
              onChange={(e) => setVal(e.target.value)}
              className="rounded-none h-11 font-mono-plex"
              data-testid="new-deadline-input"
            />
            <div className="text-[11px] text-neutral-500">
              Times are in your local timezone. Bidders will see this converted to their own timezone.
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">
              Reason for extension (optional)
            </Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. additional vendors invited, awaiting clarifications"
              className="rounded-none h-11"
              data-testid="extend-reason-input"
              maxLength={200}
            />
            <div className="text-[11px] text-neutral-500">
              Shown in the email to bidders. Keep it short and factual.
            </div>
          </div>

          {history.length > 0 && (
            <div className="border-t border-black/10 pt-3" data-testid="deadline-history">
              <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-2 font-semibold">
                Previous changes ({history.length})
              </div>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {history.slice().reverse().map((h, i) => (
                  <div key={i} className="text-[11px] text-neutral-600 font-mono-plex leading-relaxed">
                    <div>
                      <span className="text-neutral-400">
                        {h.at ? new Date(h.at).toLocaleString() : "—"}
                      </span>
                      {" · "}
                      <span>{h.by_name || h.by_email}</span>
                    </div>
                    <div className="text-neutral-500">
                      {fmtDeadline(h.from)} → <span className="text-black">{fmtDeadline(h.to)}</span>
                      {h.reason ? <span className="text-neutral-500"> · {h.reason}</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={busy || !val}
            className="rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold btn-sharp"
            data-testid="extend-deadline-confirm"
          >
            {busy ? "Updating..." : "Update Deadline"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const CloseAuctionDialog = ({ auctionId, hasBids, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const close = async (declareWinner) => {
    setBusy(true);
    try {
      await api.post(`/auctions/${auctionId}/close`, { declare_winner: declareWinner });
      toast.success(declareWinner && hasBids ? "Auction closed — winner declared" : "Auction closed without a winner");
      setOpen(false);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full rounded-none border-black h-11 btn-sharp" data-testid="open-close-dialog">
          <Gavel size={14} weight="bold" className="mr-2" /> Close Auction
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none" data-testid="close-auction-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest">Close Auction</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-neutral-700">
          {hasBids
            ? "Choose how you want to close this auction. This action cannot be undone."
            : "There are no bids yet. Closing will end the auction without a winner."}
        </p>
        {hasBids && (
          <div className="mt-2 grid gap-3 text-sm">
            <div className="border border-black/10 p-3">
              <div className="font-semibold mb-0.5">Declare a winner</div>
              <div className="text-xs text-neutral-600">Lowest bidder across all rounds wins. Ties go to whoever submitted first. Notifications sent to winner and creator.</div>
            </div>
            <div className="border border-black/10 p-3">
              <div className="font-semibold mb-0.5">Close without a winner</div>
              <div className="text-xs text-neutral-600">End the auction without awarding it. All bids are discarded. Useful if no bid met your requirements.</div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          {hasBids ? (
            <>
              <Button
                onClick={() => close(false)}
                disabled={busy}
                variant="outline"
                className="rounded-none border-black h-11 btn-sharp"
                data-testid="close-auction-no-winner"
              >
                {busy ? "Closing..." : "Close without winner"}
              </Button>
              <Button
                onClick={() => close(true)}
                disabled={busy}
                className="rounded-none bg-[#0B1929] text-white h-11 btn-sharp"
                data-testid="close-auction-confirm"
              >
                {busy ? "Closing..." : "Declare winner & close"}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => close(false)}
              disabled={busy}
              className="rounded-none bg-[#0B1929] text-white h-11 btn-sharp"
              data-testid="close-auction-confirm"
            >
              {busy ? "Closing..." : "Confirm Close"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
